import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import api from "../Utils/Axios";

function Team() {
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get company language from localStorage
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    
    // Listen for language updates
    const handleStorageChange = () => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      setCompanyLanguage(updatedLang);
    };
    
    window.addEventListener('companyDataUpdated', handleStorageChange);
    
    // Check periodically
    const interval = setInterval(() => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      if (updatedLang !== companyLanguage) {
        setCompanyLanguage(updatedLang);
      }
    }, 2000);

    return () => {
      window.removeEventListener('companyDataUpdated', handleStorageChange);
      clearInterval(interval);
    };
  }, [companyLanguage]);

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/admin/team-members");
      setTeamMembers(response.data.teamMembers || []);
    } catch (error) {
      console.error("Fetch team members error:", error);
      setTeamMembers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const isUrdu = companyLanguage === 'ur';

  return (
    <>
      <Helmet>
        <title>{companyLanguage === 'ur' ? 'ٹیم ممبرز - Galla Mandi Bills & Khata' : 'Team Members - Galla Mandi Bills & Khata'}</title>
      </Helmet>
      
      <div
        className={`${isUrdu ? 'font-urdu' : ''} bg-brand-background min-h-screen`}
        style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
        dir={isUrdu ? 'rtl' : 'ltr'}
      >
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-brand-header to-green-700 text-white py-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold mb-4">
              {companyLanguage === 'ur' ? 'ہماری ٹیم' : 'Our Team'}
            </h1>
            <p className="text-xl opacity-90">
              {companyLanguage === 'ur' 
                ? 'ہمارے ماہرین جو آپ کی خدمت کے لیے وقف ہیں' 
                : 'Meet our experts dedicated to serving you'}
            </p>
          </div>
        </section>

        {/* Team Members Section */}
        <section className={`py-20 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
          <div className="container mx-auto px-4">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto"></div>
                <p className="mt-4 text-gray-600">
                  {companyLanguage === 'ur' ? 'ٹیم ممبرز لوڈ ہو رہے ہیں...' : 'Loading team members...'}
                </p>
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">
                  {companyLanguage === 'ur' ? 'کوئی ٹیم ممبر نہیں ملا' : 'No team members found'}
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {teamMembers.map((member) => (
                  <div
                    key={member._id}
                    className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300"
                  >
                    {member.image ? (
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-32 h-32 mx-auto mb-4 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-32 h-32 mx-auto mb-4 bg-gradient-to-br from-brand-header to-green-600 rounded-full flex items-center justify-center text-white text-4xl font-bold">
                        {member.name.charAt(0)}
                      </div>
                    )}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {isUrdu && member.nameUrdu ? member.nameUrdu : member.name}
                    </h3>
                    <p className="text-gray-600">
                      {isUrdu && member.titleUrdu ? member.titleUrdu : member.title}
                    </p>
                    {member.bio && (
                      <p className="text-sm text-gray-500 mt-2">
                        {isUrdu && member.bioUrdu ? member.bioUrdu : member.bio}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}

export default Team;

